<template>
  <div v-if="isVisible && remoteConfig" class="onboarding-chatbot" :class="{ 'is-open': open }">
    <!-- Floating launcher -->
    <button
      v-show="!open"
      type="button"
      class="onboarding-chatbot__launcher"
      aria-label="Open onboarding assistant"
      @click="openPanel">
      <b-icon icon="chat-question" size="is-medium" />
      <span class="onboarding-chatbot__launcher-label">{{ assistant.launcher }}</span>
    </button>

    <!-- Chat panel -->
    <transition name="chatbot-fade">
      <section
        v-if="open"
        class="onboarding-chatbot__panel"
        role="dialog"
        aria-label="Onboarding assistant">
        <!-- Header -->
        <header class="onboarding-chatbot__header">
          <div>
            <p class="has-text-weight-bold">{{ assistant.title }}</p>
            <p class="is-size-7 has-text-grey-light">{{ assistant.subtitle }}</p>
          </div>
          <div class="onboarding-chatbot__header-actions">
            <button
              type="button"
              class="onboarding-chatbot__icon-button"
              aria-label="Reset conversation"
              @click="resetConversation">
              <b-icon icon="refresh" size="is-small" />
            </button>
            <button
              type="button"
              class="onboarding-chatbot__icon-button"
              aria-label="Close assistant"
              @click="closePanel">
              <b-icon icon="close" size="is-small" />
            </button>
          </div>
        </header>

        <!-- Messages -->
        <div ref="thread" class="onboarding-chatbot__thread">
          <template v-for="(message, index) in messages">
            <!-- Divider between navigation steps to keep the thread tidy -->
            <div
              v-if="index > 0 && message.kind === 'menu'"
              :key="`divider-${message.id}`"
              class="onboarding-chatbot__divider">
            </div>
            <div
              :key="message.id"
              class="onboarding-chatbot__message"
              :class="`onboarding-chatbot__message--${message.role}`">
            <div
              class="onboarding-chatbot__bubble"
              :class="{ 'onboarding-chatbot__bubble--menu': message.kind === 'menu' }">
              <!-- Plain text / question -->
              <div v-if="message.kind === 'text'" v-html="message.text"></div>

              <!-- Answer card -->
              <div v-else-if="message.kind === 'answer'">
                <p class="has-text-weight-bold mb-xs">{{ message.entry.title }}</p>
                <p class="onboarding-chatbot__category">{{ message.entry.group }}</p>

                <!-- Video answer -->
                <div v-if="message.entry.type === 'video' && message.entry.video" class="mt-sm">
                  <video
                    class="onboarding-chatbot__video"
                    controls
                    preload="metadata"
                    :poster="thumbnailFor(message.entry.video.publicId)"
                    :src="videoFor(message.entry.video.publicId)">
                  </video>
                  <p v-if="message.entry.video.caption" class="is-size-7 has-text-grey mt-xs">
                    {{ message.entry.video.caption }}
                    <span v-if="message.entry.video.duration"> &middot; {{ message.entry.video.duration }}</span>
                  </p>
                </div>

                <!-- Text body (always rendered when present) -->
                <div v-if="message.entry.answer" class="content is-small mt-sm" v-html="message.entry.answer"></div>

                <!-- Source document link -->
                <p v-if="message.entry.sourceUrl" class="is-size-7 mt-sm">
                  <a :href="message.entry.sourceUrl" target="_blank" rel="noopener">
                    {{ message.entry.sourceLabel || 'Open the full guide' }} &rarr;
                  </a>
                </p>
              </div>

              <!-- Fallback -->
              <div v-else-if="message.kind === 'fallback'">
                <p class="has-text-weight-bold mb-xs">{{ message.title }}</p>
                <p class="is-size-7">{{ message.body }}</p>
              </div>

              <!-- Greeting -->
              <div v-else-if="message.kind === 'greeting'">
                <p class="has-text-weight-bold mb-xs">{{ message.title }}</p>
                <p class="is-size-7">{{ message.body }}</p>
              </div>

              <!-- Menu drill-down prompt -->
              <div v-else-if="message.kind === 'menu'">
                <p class="is-size-7">{{ message.body }}</p>
              </div>
            </div>

            <!-- Quick replies under bot messages: topics first -->
            <div
              v-if="message.role === 'bot' && topicChips(message).length"
              class="onboarding-chatbot__chips">
              <button
                v-for="(reply, index) in topicChips(message)"
                :key="index"
                type="button"
                class="onboarding-chatbot__chip"
                @click="handleQuickReply(reply)">
                {{ reply.label || reply }}
              </button>
            </div>

            <!-- Navigation chips (Back / Main menu) always on their own row -->
            <div
              v-if="message.role === 'bot' && navChips(message).length"
              class="onboarding-chatbot__chips onboarding-chatbot__chips--nav">
              <button
                v-for="(reply, index) in navChips(message)"
                :key="`nav-${index}`"
                type="button"
                class="onboarding-chatbot__chip onboarding-chatbot__chip--nav"
                @click="handleQuickReply(reply)">
                {{ reply.label || reply }}
              </button>
            </div>
            </div>
          </template>

          <!-- Typing indicator -->
          <div v-if="thinking" class="onboarding-chatbot__message onboarding-chatbot__message--bot">
            <div class="onboarding-chatbot__bubble onboarding-chatbot__bubble--typing">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
        </div>

        <!-- Composer -->
        <form class="onboarding-chatbot__composer" @submit.prevent="submitInput">
          <input
            v-model="input"
            type="text"
            class="onboarding-chatbot__input"
            placeholder="Ask about onboarding..."
            aria-label="Ask about onboarding"
            :disabled="thinking" />
          <button
            type="submit"
            class="onboarding-chatbot__send"
            :disabled="!input.trim() || thinking"
            aria-label="Send message">
            <b-icon icon="send" size="is-small" />
          </button>
        </form>
      </section>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Fuse from 'fuse.js'

const FUSE_OPTIONS = {
  includeScore: true,
  threshold: 0.45,
  ignoreLocation: true,
  minMatchCharLength: 2,
  keys: [
    { name: 'title', weight: 0.45 },
    { name: 'keywords', weight: 0.45 },
    { name: 'group', weight: 0.05 },
    { name: 'answer', weight: 0.05 }
  ]
}

const ACCEPT_SCORE = 0.8

// Routes where the assistant should not appear at all (e.g. the context-switch
// page, which sits outside any merchant/supplier workspace).
const HIDDEN_ROUTES = ['context']

// Cross-cutting group that should always sit at the bottom of a category.
const HELP_GROUP = 'Help & Support'

// Maps a route to one of the four categories so that page's category can be
// pre-opened. First match wins; an unmatched route (null) opens the full
// category menu with nothing pre-selected.
const ROUTE_CATEGORY_RULES = [
  { prefix: 'onboarding', category: 'onboarding' },
  { prefix: 'shopify.onboard', category: 'onboarding' },
  { prefix: 'order', category: 'orders' },
  { prefix: 'orders', category: 'orders' },
  { prefix: 'shipments', category: 'orders' },
  { prefix: 'returns', category: 'orders' },
  { prefix: 'cancels', category: 'orders' },
  { prefix: 'exceptions', category: 'orders' },
  { prefix: 'catalog', category: 'products' },
  { prefix: 'proposal', category: 'products' },
  { prefix: 'proposals', category: 'products' },
  { prefix: 'merchandise', category: 'products' },
  { prefix: 'settings', category: 'settings' },
  { prefix: 'edi', category: 'settings' }
]

const categoryForRoute = (routeName) => {
  if (!routeName) return null
  const rule = ROUTE_CATEGORY_RULES.find(r => routeName === r.prefix || routeName.startsWith(`${r.prefix}.`))
  return rule ? rule.category : null
}

let messageCounter = 0
const nextId = () => `m-${Date.now()}-${++messageCounter}`

export default {
  name: 'OnboardingChatbot',
  data () {
    return {
      open: false,
      input: '',
      thinking: false,
      messages: [],
      // The category / group the user is currently browsing in the drill-down,
      // so an answer opened from it can offer a "Back" chip to that same list.
      currentCategory: null,
      currentGroup: null
    }
  },
  computed: {
    ...mapGetters(['api', 'auth', 'context', 'assistantConfig']),
    // The knowledgebase is currently supplier-only content, so only show the
    // assistant in a supplier (vendor) context. Staff-only by design for v1;
    // drop the auth.isStaff check to release it to all suppliers. Merchant
    // content can be added later with role-aware entries.
    isVisible () {
      return !this.isHiddenRoute && this.context.isSupplier() && this.auth.isStaff
    },
    isHiddenRoute () {
      return HIDDEN_ROUTES.includes(this.$route && this.$route.name)
    },
    // Server-backed knowledgebase (GET /v1/assistant/config), shared through
    // the store so staff edits on the Q&A settings page are reflected here
    // immediately (see store/assistant.js).
    remoteConfig () {
      return this.assistantConfig
    },
    // Common header/greeting text, shared across every page.
    assistant () {
      return (this.remoteConfig && this.remoteConfig.assistant) || {}
    },
    categories () {
      return (this.remoteConfig && this.remoteConfig.categories) || []
    },
    entries () {
      return (this.remoteConfig && this.remoteConfig.entries) || []
    },
    // The current page's category (orders/products/settings/onboarding), or null
    // on pages that aren't one of the four — used to pre-open that category.
    activeCategory () {
      return categoryForRoute(this.$route && this.$route.name)
    },
    fuse () {
      // Search spans every entry regardless of the current page.
      return new Fuse(this.entries, FUSE_OPTIONS)
    },
    entriesById () {
      return this.entries.reduce((acc, entry) => {
        acc[entry.id] = entry
        return acc
      }, {})
    }
  },
  created () {
    if (this.isVisible) this.load()
  },
  watch: {
    // Close the assistant whenever it should no longer be shown (hidden route
    // or a non-supplier context), so it isn't left open on return. Load the
    // knowledgebase the first time it becomes visible (context/auth can
    // resolve after creation).
    isVisible (visible) {
      if (visible) this.load()
      if (!visible) this.open = false
    },
    // Only the pre-opened category changes per page, so leave an open
    // conversation intact and just refresh a closed one on next open.
    activeCategory () {
      if (!this.open) this.messages = []
    }
  },
  methods: {
    openPanel () {
      this.open = true
      if (this.messages.length === 0) {
        this.pushGreeting()
      }
      this.$nextTick(this.scrollToBottom)
    },
    // Lazy-load the knowledgebase into the store; the action fetches only once.
    load () {
      this.$store.dispatch('loadAssistantConfig')
    },
    closePanel () {
      this.open = false
    },
    resetConversation () {
      this.messages = []
      this.input = ''
      this.currentCategory = null
      this.currentGroup = null
      this.pushGreeting()
    },
    pushGreeting () {
      // Pre-open the current page's category (level 2) when we're on one of the
      // four main pages; otherwise show the four categories (level 1).
      let quickReplies
      if (this.activeCategory) {
        this.currentCategory = this.activeCategory
        this.currentGroup = null
        quickReplies = this.groupChips(this.activeCategory)
        quickReplies.push({ label: '↩ Back', go: 'root', nav: true })
      } else {
        this.currentCategory = null
        this.currentGroup = null
        quickReplies = this.rootChips()
      }
      this.messages.push({
        id: nextId(),
        role: 'bot',
        kind: 'greeting',
        title: this.assistant.greetingTitle,
        body: this.assistant.intro,
        quickReplies
      })
      this.$nextTick(this.scrollToBottom)
    },
    // --- Menu construction (category -> group -> question) ---
    categoryLabel (id) {
      const cat = this.categories.find(c => c.id === id)
      return cat ? cat.label : id
    },
    groupsForCategory (category) {
      const groups = []
      this.entries.forEach(entry => {
        if (entry.contexts.includes(category) && !groups.includes(entry.group)) {
          groups.push(entry.group)
        }
      })
      // Order priority:
      //   1. groups listed in the category's optional `groupOrder`, in that order
      //   2. everything else in first-seen order
      //   3. the cross-cutting help group pinned to the bottom (unless it was
      //      explicitly placed via groupOrder)
      const cat = this.categories.find(c => c.id === category)
      const order = (cat && cat.groupOrder) || []
      const rank = (g) => {
        const i = order.indexOf(g)
        if (i !== -1) return i
        if (g === HELP_GROUP) return 1e6
        return order.length + groups.indexOf(g)
      }
      return groups.slice().sort((a, b) => rank(a) - rank(b))
    },
    questionsFor (category, group) {
      return this.entries.filter(e => e.contexts.includes(category) && e.group === group)
    },
    rootChips () {
      return this.categories
        .filter(c => this.groupsForCategory(c.id).length)
        .map(c => ({ label: c.label, go: 'groups', category: c.id }))
    },
    groupChips (category) {
      return this.groupsForCategory(category)
        .map(group => ({ label: group, go: 'questions', category, group }))
    },
    questionChips (category, group) {
      return this.questionsFor(category, group)
        .map(entry => ({ label: entry.title, entryId: entry.id }))
    },
    pushBotMenu (body, quickReplies) {
      this.messages.push({ id: nextId(), role: 'bot', kind: 'menu', body, quickReplies })
      this.$nextTick(this.scrollToBottom)
    },
    // Split a message's chips so nav chips (Back / Main menu) render on their
    // own row beneath the topic/question chips.
    topicChips (message) {
      return (message.quickReplies || []).filter(reply => !reply.nav)
    },
    navChips (message) {
      return (message.quickReplies || []).filter(reply => reply.nav)
    },
    showRoot () {
      this.currentCategory = null
      this.currentGroup = null
      this.pushBotMenu('Choose a topic:', this.rootChips())
    },
    showGroups (category) {
      this.currentCategory = category
      this.currentGroup = null
      const chips = this.groupChips(category)
      chips.push({ label: '↩ Back', go: 'root', nav: true })
      this.pushBotMenu(`${this.categoryLabel(category)} — pick a topic:`, chips)
    },
    showQuestions (category, group) {
      this.currentCategory = category
      this.currentGroup = group
      const chips = this.questionChips(category, group)
      chips.push({ label: '↩ Back', go: 'groups', category, nav: true })
      chips.push({ label: '⌂ Main menu', go: 'root', nav: true })
      this.pushBotMenu(`${group} — pick a question:`, chips)
    },
    submitInput () {
      const text = this.input.trim()
      if (!text || this.thinking) return
      this.input = ''
      // A typed question is off-menu, so its answer shows related topics.
      this.currentGroup = null
      this.sendUserMessage(text, null, true)
    },
    handleQuickReply (reply) {
      // Drill-down chip: navigate the menu instead of asking a question.
      if (reply && reply.go) {
        if (reply.go === 'root') this.showRoot()
        else if (reply.go === 'groups') this.showGroups(reply.category)
        else if (reply.go === 'questions') this.showQuestions(reply.category, reply.group)
        return
      }
      const text = typeof reply === 'string' ? reply : reply.label
      if (reply && reply.entryId) {
        const entry = this.entriesById[reply.entryId]
        // Anchor the drill position to this entry's group so Back returns to the
        // right list — whether it was a menu leaf or a cross-topic related link.
        if (entry) {
          this.currentGroup = entry.group
          if (!entry.contexts.includes(this.currentCategory)) {
            this.currentCategory = entry.contexts[0]
          }
        }
        this.sendUserMessage(text, entry)
      } else {
        this.sendUserMessage(text)
      }
    },
    sendUserMessage (text, forcedEntry, typed = false) {
      this.messages.push({
        id: nextId(),
        role: 'user',
        kind: 'text',
        text: this.escapeHtml(text)
      })
      this.thinking = true
      this.$nextTick(this.scrollToBottom)

      setTimeout(() => {
        const entry = forcedEntry || this.findBestEntry(text)
        if (entry) {
          this.pushAnswer(entry)
        } else {
          // Only free-typed questions are worth recording; chip labels are our
          // own curated copy, so a miss there is a content bug, not a signal.
          if (typed) this.logMissedQuestion(text)
          this.pushFallback()
        }
        this.thinking = false
        this.$nextTick(this.scrollToBottom)
      }, 350)
    },
    findBestEntry (query) {
      const results = this.fuse.search(query)
      if (!results.length) return null
      const top = results[0]
      if (top.score !== undefined && top.score > ACCEPT_SCORE) return null
      return top.item
    },
    pushAnswer (entry) {
      const relatedEntries = (entry.relatedIds || [])
        .map(id => this.entriesById[id])
        .filter(Boolean)

      let quickReplies
      if (this.currentCategory && this.currentGroup) {
        // Reached via the drill-down menu. Show only cross-topic related links
        // (same-group siblings are already in the list we came from — hiding
        // them avoids ping-pong; a hub like "which integration options" links
        // out to other groups and those show here), then Back / Main menu.
        quickReplies = relatedEntries
          .filter(related => related.group !== this.currentGroup)
          .map(related => ({ label: related.title, entryId: related.id }))
        quickReplies.push({ label: '↩ Back', go: 'questions', category: this.currentCategory, group: this.currentGroup, nav: true })
        quickReplies.push({ label: '⌂ Main menu', go: 'root', nav: true })
      } else {
        quickReplies = relatedEntries
          .slice(0, 3)
          .map(related => ({ label: related.title, entryId: related.id }))
      }

      this.messages.push({
        id: nextId(),
        role: 'bot',
        kind: 'answer',
        entry,
        quickReplies
      })
    },
    // Curated suggestions shown under the fallback message: the groups of the
    // category being browsed (or the page's category), else the main menu —
    // every chip leads back into real content.
    defaultQuickReplies () {
      const category = this.currentCategory || this.activeCategory
      if (category && this.groupsForCategory(category).length) {
        const chips = this.groupChips(category)
        chips.push({ label: '⌂ Main menu', go: 'root', nav: true })
        return chips
      }
      return this.rootChips()
    },
    // Fire-and-forget: record what the supplier asked so staff can turn it into
    // a Q&A (the API upserts on normalized text and counts repeats). Never
    // blocks or surfaces errors to the supplier.
    logMissedQuestion (question) {
      const payload = { question }
      if (this.context.partner && this.context.partner.id) {
        payload.brand_id = this.context.partner.id
      }
      this.$http.post(this.api.baseUrl + '/v1/assistant/missed-questions/', payload).catch(() => {})
    },
    pushFallback () {
      const fallback = (this.remoteConfig && this.remoteConfig.fallback) || {}
      // Reuse the current page's curated suggestions so fallback chips always
      // open a real entry.
      this.messages.push({
        id: nextId(),
        role: 'bot',
        kind: 'fallback',
        title: fallback.title,
        body: fallback.body,
        quickReplies: this.defaultQuickReplies()
      })
    },
    videoFor (publicId) {
      const cloudinary = (this.remoteConfig && this.remoteConfig.cloudinary) || {}
      return `${cloudinary.videoBaseUrl}/${publicId}.mp4`
    },
    thumbnailFor (publicId) {
      const cloudinary = (this.remoteConfig && this.remoteConfig.cloudinary) || {}
      return `${cloudinary.thumbnailBaseUrl}/${publicId}.jpg`
    },
    escapeHtml (value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    scrollToBottom () {
      const el = this.$refs.thread
      if (!el) return
      el.scrollTop = el.scrollHeight
    }
  }
}
</script>

<style lang="scss" scoped src="./OnboardingChatbot.scss"></style>
